import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { listAllGroupConfigurations } from "../../api/groupConfigurationApi";
import {
  applicantList,
  waitingApplicantList,
  admissionApprovedApplicantList,
  approveForAdmission,
  reverseApproveForAdmission,
  approveForWaiting,
  reverseApproveForWaiting,
  markAsPaid,
} from "../../api/onlineAdmissionApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

/**
 * Super-admin only, fixed route — digicampusbd's hardcoded "Online
 * Admission" sidebar's "General Applicant List"/"Applicant Approve
 * Report"/"Applicant Waiting Report". See MIGRATION.md Phase 10 notes,
 * including the confirmed old-app gap this page's "Mark as Paid" action
 * works around (nothing in the real app ever set paidStatus, so no
 * applicant could ever appear here otherwise).
 */
export default function OnlineAdmissionApplicantsPage() {
  const [groupConfigs, setGroupConfigs] = useState([]);
  const [groupConfigId, setGroupConfigId] = useState("");
  const [tab, setTab] = useState("all");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    listAllGroupConfigurations().then(setGroupConfigs);
  }, []);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Online Admission — Applicants</h1>
      </div>
      <div className="attendance-filters">
        <label>
          Class (Group)
          <select value={groupConfigId} onChange={(e) => setGroupConfigId(e.target.value)}>
            <option value="">Select</option>
            {groupConfigs.map((g) => (
              <option key={g.id} value={g.id}>
                {g.classGroupName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Tabs
        tabs={[
          { key: "all", label: "General Applicant List" },
          { key: "approved", label: "Approve Report" },
          { key: "waiting", label: "Waiting Report" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {!groupConfigId && <p>Select a class (group) to continue.</p>}
      {groupConfigId && (
        <ApplicantListPanel
          key={tab + groupConfigId}
          tab={tab}
          groupConfigId={groupConfigId}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
    </MainLayout>
  );
}

function ApplicantListPanel({ tab, groupConfigId, setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = () => {
    setLoading(true);
    const request =
      tab === "all"
        ? applicantList(groupConfigId)
        : tab === "approved"
        ? admissionApprovedApplicantList(groupConfigId)
        : waitingApplicantList(groupConfigId);
    request
      .then((data) => {
        setRows(data);
        setSelectedIds([]);
      })
      .catch(() => setError("Failed to load applicants."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab, groupConfigId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === rows.length ? [] : rows.map((r) => r.id));

  const runAction = async (actionFn, successMessage) => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Select at least one applicant.");
      return;
    }
    try {
      await actionFn(selectedIds);
      setSuccess(successMessage);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Action failed.");
    }
  };

  const handleMarkPaid = async (row) => {
    setError("");
    try {
      await markAsPaid(row.id);
      setSuccess(`${row.applicantName} marked as paid.`);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to mark as paid.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <>
      <DataTable
        columns={[
          {
            key: "select",
            label: <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />,
            render: (r) => <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggle(r.id)} />,
          },
          { key: "registrationId", label: "Reg. No" },
          { key: "applicantName", label: "Applicant" },
          { key: "fatherName", label: "Father" },
          { key: "mobileNumber", label: "Mobile" },
          { key: "applicationDate", label: "Applied On" },
          { key: "paidStatus", label: "Paid", render: (r) => (r.paidStatus === 1 ? "Yes" : "No") },
          { key: "admissionStatus", label: "Approved" },
          { key: "waitingStatus", label: "Waiting" },
        ]}
        rows={rows}
        emptyMessage="No applicants found."
        actions={(row) =>
          row.paidStatus !== 1 && (
            <button type="button" className="btn-link" onClick={() => handleMarkPaid(row)}>
              Mark as Paid
            </button>
          )
        }
      />

      <div className="form-actions" style={{ justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }}>
        {tab === "all" && (
          <>
            <button type="button" className="btn-secondary" onClick={() => runAction(approveForAdmission, "Approved for admission.")}>
              Approve for Admission
            </button>
            <button type="button" className="btn-secondary" onClick={() => runAction(approveForWaiting, "Added to waiting list.")}>
              Add to Waiting List
            </button>
          </>
        )}
        {tab === "approved" && (
          <button type="button" className="btn-secondary" onClick={() => runAction(reverseApproveForAdmission, "Reversed.")}>
            Reverse Admission Approval
          </button>
        )}
        {tab === "waiting" && (
          <button type="button" className="btn-secondary" onClick={() => runAction(reverseApproveForWaiting, "Reversed.")}>
            Reverse Waiting Status
          </button>
        )}
      </div>
    </>
  );
}
